import { Controller } from "@hotwired/stimulus";
import { flashDangerAlert } from "../includes/flashAlert";

// One instance per recently-uploaded document row (see the view attaching this controller), polling
// its async structural-validation status until it settles. There is no page-level gate to hold --
// the admin document list has no wizard "Continue" step -- so an invalid result surfaces as a flash
// rather than blocking anything.
export default class extends Controller {
  static values = {
    url: String,
    filename: String,
    pollIntervalMs: { type: Number, default: 2000 },
    // Matches the 5-minute window the view itself uses to decide whether to attach this
    // controller at all (`created_at > 5.minutes.ago`) -- PersistSpreadsheetText's own comments
    // note a large workbook can take longer than a request's budget to parse, so giving up sooner
    // than the page's own poll-attach window would silently miss a slow-but-otherwise-in-window
    // verdict for no reason tied to the actual parse.
    giveUpMs: { type: Number, default: 300000 },
    // A per-attempt ceiling, separate from giveUpMs -- arming the abort timer at the full
    // remaining window would let one stalled socket (neither resolving nor rejecting) burn the
    // entire give-up budget on a single attempt, with no retry and no verdict, instead of the many
    // short attempts that budget is meant to afford.
    requestTimeoutMs: { type: Number, default: 10000 },
  };

  connect () {
    this.disconnected = false;
    this.giveUpAt = Date.now() + this.giveUpMsValue;
    this.check();
  }

  // Aborts any in-flight request too, not just a scheduled retry -- otherwise a request outliving
  // this controller (the row it belongs to leaving the DOM some other way than a full page unload)
  // could still resolve later and flash an invalid result against whatever page the admin has since
  // moved to. abort() itself only rejects the fetch promise, though -- disconnected is what stops
  // that rejection (indistinguishable, at the catch, from an ordinary network failure) from
  // scheduling yet another check for a controller that no longer exists.
  disconnect () {
    this.disconnected = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.abortController) this.abortController.abort();
  }

  check () {
    if (this.disconnected) return;

    const remaining = this.giveUpAt - Date.now();
    if (remaining <= 0) return;

    // A dead connection leaves `fetch` neither resolved nor rejected -- with no timeout of its
    // own, nothing would ever call check() again to notice the give-up deadline has passed, and
    // this row would poll forever instead of quietly stopping at its own ceiling. Capped at
    // requestTimeoutMs, not just `remaining`, so a single stalled attempt aborts and retries
    // well within the give-up window instead of consuming the whole thing itself; `catch` below
    // reschedules unconditionally, and scheduleNextCheck's own `remaining <= 0` check is what
    // actually stops it once the real deadline arrives. Stored on the instance, not a local, so
    // disconnect() can also reach it directly.
    this.abortController = new AbortController();
    const abortTimer = setTimeout(() => this.abortController.abort(), Math.min(this.requestTimeoutMsValue, remaining));

    fetch(this.urlValue, { headers: { Accept: "application/json" }, signal: this.abortController.signal })
      .then((response) => response.json())
      .then((data) => {
        clearTimeout(abortTimer);
        if (this.disconnected) return;
        this.applyStatus(data);
      })
      .catch(() => {
        clearTimeout(abortTimer);
        if (this.disconnected) return;
        this.scheduleNextCheck();
      });
  }

  applyStatus (data) {
    if (data.status === "invalid") {
      this.flashInvalid(data.error_message);
    } else if (data.status !== "valid") {
      // "pending", or any other shape (a transient JSON-formatted 500, an unrecognized body) --
      // retry rather than silently treating an unrecognized response the same as a settled check.
      // Only a genuine "valid" is reason enough to stop: nothing else here proves the real check
      // ever completed, and stopping on anything less would let a later invalid verdict go unseen.
      this.scheduleNextCheck();
    }
  }

  // Capped at the remaining time rather than always the full interval, so a "pending" that lands
  // just short of the deadline checks back in at the deadline itself instead of drifting up to one
  // interval past it.
  scheduleNextCheck () {
    const remaining = this.giveUpAt - Date.now();
    if (remaining <= 0) return;

    this.timer = setTimeout(() => this.check(), Math.min(this.pollIntervalMsValue, remaining));
  }

  // Named, not just "File upload error" -- with several rows polling at once, that generic header
  // alone doesn't tell the admin which workbook needs to be reopened or replaced.
  flashInvalid (message) {
    flashDangerAlert(`Upload error: ${this.filenameValue}`, message);
  }
}
